import type {
  KnowledgeAiPreparationStatus,
  KnowledgeFailureReason,
  KnowledgeItemData,
  KnowledgeItemStatus,
  KnowledgePagePreview,
  KnowledgeUrlFailureReason,
} from "./api";

export const KNOWLEDGE_MAX_PDF_SIZE_BYTES = 20 * 1024 * 1024;
export const KNOWLEDGE_MAX_URL_LENGTH_CHARS = 2048;

type KnowledgeAiPreparationProjection = {
  ai_preparation_status?: KnowledgeAiPreparationStatus | null;
  ai_preparation_failure_code?: string | null;
  can_retry_ai_preparation?: boolean;
};

const KNOWLEDGE_URL_CONTROL_CHARACTER_PATTERN = new RegExp("\\p{C}", "u");
const KNOWLEDGE_URL_SECRET_QUERY_KEY_PATTERN =
  "(?:token|access_token|id_token|refresh_token|api_key|apikey|signature|sig|auth|session|secret|password|passwd|code|credential|authorization|jwt|x-amz-(?:credential|signature)|x-goog-signature)";

const STATUS_LABELS: Record<KnowledgeItemStatus, string> = {
  created: "作成中",
  uploading: "アップロード中",
  uploaded: "処理待ち",
  processing: "処理中",
  ready: "利用可能",
  failed: "処理に失敗",
  deleted: "削除済み",
};

const FAILURE_MESSAGES: Readonly<Partial<Record<KnowledgeFailureReason, string>>> = {
  page_count_exceeded: "ページ数が上限を超えています。50ページ以内のPDFをお試しください。",
  scanned_pdf_not_supported: "このPDFは画像として保存されている可能性があります。文字情報を含むPDFで再度お試しください。",
  pdf_source_not_found: "アップロードされたファイルを確認できませんでした。もう一度お試しください。",
  pdf_parse_failed: "PDFの読み取りに失敗しました。別のPDFでお試しください。",
  pdf_text_extraction_failed: "PDFの文字抽出に失敗しました。別のPDFでお試しください。",
};

const URL_FAILURE_MESSAGES: Readonly<Record<KnowledgeUrlFailureReason, string>> = {
  url_invalid: "URLが無効です。",
  url_blocked_address: "安全上の理由でこのURLは取得できません。",
  url_dns_failed: "URLの接続先を確認できませんでした。",
  url_timeout: "Webページの取得がタイムアウトしました。",
  url_ssl_failed: "Webページの安全な接続を確認できませんでした。",
  url_too_many_redirects: "リダイレクト回数が上限を超えました。",
  url_redirect_downgrade: "安全でないリダイレクトを検出しました。",
  url_response_too_large: "Webページのサイズが上限を超えています。",
  url_pdf_detected: "PDF形式のURLは現在取り込めません。",
  url_unsupported_content_type: "このWebページの形式には対応していません。",
  url_http_client_error: "Webページを取得できませんでした。",
  url_http_server_error: "Webページ側のエラーで取得できませんでした。",
  url_empty_content: "Webページから本文を取得できませんでした。",
  url_extraction_failed: "Webページの本文を抽出できませんでした。",
  url_internal_error: "Webページの処理中にエラーが発生しました。",
  url_queue_dispatch_failed: "Webページの処理を開始できませんでした。",
};

const URL_FAILURE_FALLBACK = "Webページを読み取れませんでした。";

const AI_PREPARATION_FAILURE_MESSAGES: Readonly<Record<string, string>> = {
  summary_dispatch_failed: "AIで使うための準備を開始できませんでした。",
  summary_generation_failed: "AIで使うための要約を作成できませんでした。",
  summary_job_failed: "AIで使うための要約処理を完了できませんでした。",
  summary_stale: "Webページの更新を確認できなかったため、準備を完了できませんでした。",
};

const AI_PREPARATION_FAILURE_FALLBACK = "AIで使うための準備を完了できませんでした。";

export function getKnowledgeStatusLabel(status: KnowledgeItemStatus | string | null | undefined): string {
  return STATUS_LABELS[(status ?? "") as KnowledgeItemStatus] ?? "状態を確認中";
}

export function getKnowledgeTypeLabel(type: string | null | undefined): string {
  if (type === "pdf") return "PDF";
  if (type === "executive_memo") return "社長メモ";
  if (type === "url") return "Webページ";
  if (type === "company_research") return "企業リサーチ";
  if (type === "deep_report") return "深掘りレポート";
  return "ナレッジ";
}

export function getKnowledgeSummaryStatusLabel(
  item: Pick<KnowledgeItemData, "has_summary" | "summarized_at"> | null | undefined
): string {
  return item?.has_summary || item?.summarized_at ? "要約済み" : "要約待ち";
}

export function shouldShowKnowledgeSummaryStatus(
  item: (Pick<KnowledgeItemData, "type" | "status"> & KnowledgeAiPreparationProjection) | null | undefined
): boolean {
  if (!item) return false;
  if (item.type !== "url") return true;

  return item.status !== "failed" && item.ai_preparation_status !== "failed";
}

export function getKnowledgeFailureMessage(
  reason: KnowledgeFailureReason | string | null | undefined,
  type: string | null | undefined = "pdf"
): string {
  if (type === "url") {
    if (!reason) return URL_FAILURE_FALLBACK;
    return URL_FAILURE_MESSAGES[reason as KnowledgeUrlFailureReason] ?? URL_FAILURE_FALLBACK;
  }

  const fallback =
    type === "pdf"
      ? "PDFの読み取りに失敗しました。別のPDFでお試しください。"
      : "ナレッジの読み取りに失敗しました。時間を置いてもう一度お試しください。";
  if (!reason) return fallback;
  return FAILURE_MESSAGES[reason as KnowledgeFailureReason] ?? fallback;
}

export function getKnowledgeItemDisplayName(
  item: Pick<KnowledgeItemData, "title" | "original_filename"> & { type?: string | null }
): string {
  if (item.type === "url") {
    return sanitizeKnowledgeUrlDisplayTitle(item.title) || "Webページ";
  }

  return item.title?.trim() || item.original_filename?.trim() || (item.type === "deep_report" ? "深掘りレポート" : "名称未設定");
}

function sanitizeKnowledgeUrlDisplayTitle(value: string | null | undefined): string | null {
  if (!value || KNOWLEDGE_URL_CONTROL_CHARACTER_PATTERN.test(value)) return null;
  const normalized = value.replace(/[\p{Z}\s]+/gu, " ").trim();
  if (!normalized || looksLikeKnowledgeUrl(normalized)) return null;
  return Array.from(normalized).slice(0, 255).join("");
}

function looksLikeKnowledgeUrl(value: string): boolean {
  if (/%(?:25)*(?:2f|3a|3f|23|3d|26|5c)/iu.test(value)) return true;

  const views = [value];
  let decoded = value;
  for (let round = 0; round < 5; round += 1) {
    let next: string;
    try {
      next = decodeURIComponent(decoded);
    } catch {
      break;
    }
    if (next === decoded || KNOWLEDGE_URL_CONTROL_CHARACTER_PATTERN.test(next)) break;
    views.push(next);
    decoded = next;
  }

  const standaloneSecret = new RegExp(`^\\s*[?#]?${KNOWLEDGE_URL_SECRET_QUERY_KEY_PATTERN}\\s*=\\s*\\S+\\s*$`, "iu");
  const relativeSecretQuery = new RegExp(
    `^\\s*[^\\s/?#&=]+(?:/[^\\s?#]*)?\\?${KNOWLEDGE_URL_SECRET_QUERY_KEY_PATTERN}=[^\\s#]*(?:#[^\\s]*)?\\s*$`,
    "iu"
  );

  return views.some((view) =>
    /[a-z][a-z0-9+.-]*:\/\/[^\s]+/iu.test(view)
    || /[a-z][a-z0-9+.-]*:\\{2}[^\s]+/iu.test(view)
    || /^\s*(?:mailto|data|javascript|file):[^\s]+\s*$/iu.test(view)
    || /(?:^|[\s(<（「『])(?:\/{2}|\\{2})[^\s]+/u.test(view)
    || /(?:^|[\s(<（「『])(?:(?:www\.)?(?:[\p{L}\p{N}](?:[\p{L}\p{N}-]{0,62})\.)+[\p{L}](?:[\p{L}\p{N}-]{1,62})|(?:[0-9]{1,3}\.){3}[0-9]{1,3}|localhost)(?::[0-9]{1,5}|[/\\?#][^\s]*)/iu.test(view)
    || /^\s*(?:\/(?!\/)|\\(?!\\)|\.\/|\.\.\/)\S+\s*$/u.test(view)
    || relativeSecretQuery.test(view)
    || standaloneSecret.test(view)
  );
}

export function getKnowledgeFailureDisplayMessage(
  item: Pick<KnowledgeItemData, "failure_code" | "failure_message" | "failure_reason" | "type">
): string {
  if (item.type === "url") {
    return getKnowledgeFailureMessage(item.failure_code || item.failure_reason, item.type);
  }

  return item.failure_message?.trim()
    || getKnowledgeFailureMessage(item.failure_reason, item.type)
    || "処理を完了できませんでした。";
}

export function getKnowledgeAiPreparationFailureMessage(
  failureCode: string | null | undefined
): string {
  if (!failureCode) return AI_PREPARATION_FAILURE_FALLBACK;
  return AI_PREPARATION_FAILURE_MESSAGES[failureCode] ?? AI_PREPARATION_FAILURE_FALLBACK;
}

export function isKnowledgeItemAiPreparationFailed(
  item: (Pick<KnowledgeItemData, "status" | "type"> & KnowledgeAiPreparationProjection) | null | undefined
): boolean {
  return Boolean(
    item
    && item.type === "url"
    && item.status === "ready"
    && item.ai_preparation_status === "failed"
  );
}

export function canRetryKnowledgeItemAiPreparation(
  item: (Pick<KnowledgeItemData, "status" | "type"> & KnowledgeAiPreparationProjection) | null | undefined
): boolean {
  return isKnowledgeItemAiPreparationFailed(item) && item?.can_retry_ai_preparation === true;
}

export function isKnowledgeItemAiReferenceableType(type: string | null | undefined): boolean {
  return type === "pdf" || type === "executive_memo" || type === "company_research" || type === "deep_report";
}

export function isKnowledgeItemUsable(
  item: Pick<KnowledgeItemData, "status" | "type" | "text_extractable" | "ai_processing_allowed"> | null | undefined
): boolean {
  return Boolean(
    item &&
    item.status === "ready" &&
    isKnowledgeItemAiReferenceableType(item.type) &&
    item.text_extractable === true &&
    item.ai_processing_allowed === true
  );
}

export function isKnowledgeItemLibraryAvailable(
  item: (Pick<KnowledgeItemData, "status" | "type" | "text_extractable" | "ai_processing_allowed">
    & KnowledgeAiPreparationProjection
    & { failure_reason?: KnowledgeFailureReason | string | null }) | null | undefined
): boolean {
  if (!item) return false;
  if (item.type !== "url") return isKnowledgeItemUsable(item);

  return item.status === "ready"
    && item.text_extractable === true
    && item.ai_processing_allowed === true
    && !item.failure_reason
    && (item.ai_preparation_status === "ready" || item.ai_preparation_status == null);
}

export function isKnowledgeItemGroupTalkUsable(
  item: (Pick<
      KnowledgeItemData,
      "status" | "type" | "text_extractable" | "ai_processing_allowed" | "failure_reason"
    > & KnowledgeAiPreparationProjection) | null | undefined
): boolean {
  if (!item) return false;
  return item.type === "url" ? isKnowledgeItemLibraryAvailable(item) : isKnowledgeItemUsable(item);
}

export function isKnowledgeItemCompanyResearchUsable(
  item: (Pick<
      KnowledgeItemData,
      "status" | "type" | "text_extractable" | "ai_processing_allowed" | "failure_reason"
    > & KnowledgeAiPreparationProjection) | null | undefined
): boolean {
  if (!item || item.type === "deep_report") return false;
  return item.type === "url" ? isKnowledgeItemLibraryAvailable(item) : isKnowledgeItemUsable(item);
}

export function getKnowledgeLibraryStatusLabel(
  item: (Pick<KnowledgeItemData, "status" | "type" | "text_extractable" | "ai_processing_allowed">
    & KnowledgeAiPreparationProjection) | null | undefined
): string {
  if (!item) return getKnowledgeStatusLabel(null);
  if (item.type === "url" && isKnowledgeItemPollingStatus(item.status)) {
    return "処理中";
  }
  if (isKnowledgeItemAiPreparationFailed(item)) {
    return "準備に失敗";
  }
  if (item.type === "url" && item.status === "ready" && item.ai_preparation_status === "processing") {
    return "準備中";
  }
  if (isKnowledgeItemLibraryAvailable(item)) {
    return "利用可能";
  }
  if (item.type === "url" && item.status === "ready" && !isKnowledgeItemLibraryAvailable(item)) {
    return "準備中";
  }

  return getKnowledgeStatusLabel(item.status);
}

export function getKnowledgeUnavailableReason(
  item: (Pick<KnowledgeItemData, "status" | "failure_reason" | "type" | "text_extractable" | "ai_processing_allowed">
    & KnowledgeAiPreparationProjection) | null | undefined
): string {
  if (!item || isKnowledgeItemGroupTalkUsable(item)) return "";
  if (item.status === "failed") return getKnowledgeFailureMessage(item.failure_reason, item.type);
  if (item.status === "deleted") return "このナレッジは削除済みです。";
  if (isKnowledgeItemPending(item)) {
    return "処理が完了すると、このナレッジを参考資料として利用できます。";
  }
  if (item.status === "ready" && item.type !== "url" && !isKnowledgeItemAiReferenceableType(item.type)) {
    return "この種類のナレッジは参考資料として利用できません。";
  }
  if (item.status === "ready" && item.text_extractable !== true) {
    return "本文を読み取れるナレッジだけ参考資料として利用できます。";
  }
  if (item.status === "ready" && item.ai_processing_allowed !== true) {
    return "AI処理が許可されているナレッジだけ参考資料として利用できます。";
  }
  return "処理が完了すると、このナレッジを参考資料として利用できます。";
}

export function isKnowledgeItemPollingStatus(status: KnowledgeItemStatus | string | null | undefined): boolean {
  return status === "uploaded" || status === "processing";
}

export function isKnowledgeItemPending(
  item: (Pick<
      KnowledgeItemData,
      "status" | "type" | "text_extractable" | "ai_processing_allowed" | "failure_reason"
    > & KnowledgeAiPreparationProjection) | null | undefined
): boolean {
  if (!item) return false;
  if (isKnowledgeItemPollingStatus(item.status)) return true;

  if (item.type !== "url" || item.status !== "ready") return false;
  if (item.ai_preparation_status === "processing") return true;
  if (item.ai_preparation_status === "ready" || item.ai_preparation_status === "failed") return false;

  return item.text_extractable === true
    && item.ai_processing_allowed !== true
    && !item.failure_reason;
}

export function isKnowledgeItemPreviewable(
  item: Pick<KnowledgeItemData, "status" | "text_extractable"> | null | undefined
): boolean {
  return item?.status === "ready" && item.text_extractable === true;
}

export function getKnowledgeAiUsageStatusLabel(
  item: (Pick<
    KnowledgeItemData,
    "status" | "type" | "text_extractable" | "ai_processing_allowed" | "failure_reason"
  > & KnowledgeAiPreparationProjection) | null | undefined
): string {
  if (!item) return "状態を確認中";
  if (item.status === "uploaded" || item.status === "processing") return "処理中";
  if (isKnowledgeItemAiPreparationFailed(item)) return "準備に失敗";
  if (isKnowledgeItemGroupTalkUsable(item)) return "AIで利用可能";
  if (isKnowledgeItemPending(item)) return "準備中";
  return "利用不可";
}

export function getKnowledgeStructureLabel(type: string | null | undefined, pageCount: number | null | undefined): string | null {
  if (!pageCount || pageCount < 1) return null;
  if (type === "deep_report") {
    return pageCount === 1 ? "概要" : `概要＋${pageCount - 1}セクション`;
  }
  return `${pageCount}ページ`;
}

export function getKnowledgePagePreviewLabel(
  type: string | null | undefined,
  page: Pick<KnowledgePagePreview, "page_no" | "chunk_index">,
  index: number
): string {
  if (type === "deep_report") {
    const pageNumber = page.page_no && page.page_no > 0 ? page.page_no : page.chunk_index + 1;
    return pageNumber === 1 ? "概要" : `セクション ${pageNumber - 1}`;
  }

  if (type === "url" || type === "company_research") {
    const sectionNumber = page.page_no && page.page_no > 0
      ? page.page_no
      : page.chunk_index + 1;
    return `セクション ${sectionNumber}`;
  }

  if (page.page_no && page.page_no > 0) {
    return `ページ ${page.page_no}`;
  }

  return `本文 ${index + 1}`;
}

export function getKnowledgeBusinessPoints(value: string | null | undefined): string[] {
  if (!value) return [];
  return value
    .split(/\r?\n/u)
    .map((point) => point.replace(/^\s*(?:[-*・]|\d+[.)、])\s*/u, "").trim())
    .filter((point) => point.length > 0);
}

export function validateKnowledgePdfFile(file: File): string | null {
  const filename = file.name.trim().toLowerCase();
  const mimeType = file.type.trim().toLowerCase();

  if (!filename.endsWith(".pdf") || (mimeType !== "" && mimeType !== "application/pdf")) {
    return "PDFファイルを選んでください。";
  }

  if (file.size > KNOWLEDGE_MAX_PDF_SIZE_BYTES) {
    return "20MB以内のPDFを選んでください。";
  }

  return null;
}

export type KnowledgeUrlValidationResult = {
  url: string;
  error: string | null;
};

export function validateKnowledgeUrlInput(value: string): KnowledgeUrlValidationResult {
  if (KNOWLEDGE_URL_CONTROL_CHARACTER_PATTERN.test(value)) {
    return {
      url: value,
      error: "URLに改行や制御文字は使用できません。",
    };
  }

  const url = value.replace(/^ +| +$/g, "");
  if (url === "") {
    return {
      url,
      error: "URLを入力してください。",
    };
  }

  if (Array.from(url).length > KNOWLEDGE_MAX_URL_LENGTH_CHARS) {
    return {
      url,
      error: "URLは2,048文字以内で入力してください。",
    };
  }

  const schemeMatch = url.match(/^([A-Za-z][A-Za-z0-9+.-]*):/);
  if (!schemeMatch) {
    return {
      url,
      error: "http:// または https:// から始まるURLを入力してください。",
    };
  }

  if (!/^https?:\/\//i.test(url)) {
    return {
      url,
      error: "http:// または https:// のURLを入力してください。",
    };
  }

  return { url, error: null };
}
